/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

// Libraries
import React, { useState, useEffect } from 'react'
import firebase from 'firebase/app'
import 'firebase/database'
import 'firebase/auth'

// TA components
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/button.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/form.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/modal.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/popup.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/checkbox.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/divider.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/card.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/transition.css'

import './styles.scss'
import useSize from 'hooks/useSize'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { Route, useHistory, useLocation } from 'react-router-dom'

// Custom components
import { ArticleTeaser } from '../articleLink'
import { CollaborativeMap } from 'components/collaborativeMap'
import { AdminToolbar } from 'components/adminToolbar'
import { AdminMarkersList } from 'components/adminMarkersList'
import { MapBox } from 'components/mapBox'
import { RestosList } from 'components/restosList'
import { InfoLayer } from 'components/infoLayer'
import { Filters } from 'components/filters'

import {
  signIn,
  signOut,
  adminUserConnected,
  getAdminDisplayName,
  clientCheckAdminEmail
} from 'lib/adminHelper'
import { adminDisableMarker, adminDeleteMarker } from 'lib/model'
import fr from 'locales/fr.yaml'

const ARTICLE_ID = '219418627283'
const localisationStrings = {
  fr
}
const locale = 'fr'
const isMobile = window.innerWidth < 768

export function AppComponent() {
  const [container, size] = useSize()
  const [markers, setMarkers] = useState([])
  const [statistics, setStatistics] = useState([])
  const [activeMarker, setActiveMarker] = useState(null)
  const [filteredMarkers, setFilteredMarkers] = useState([])
  const [mapIsLocked, setMapIsLocked] = useState(true)
  const [map, setMap] = useState(null)

  // Admin variables
  const [userId, setUserId] = useState(null)
  const [isAdminConnected, setIsAdminConnected] = useState(false)
  const [adminUsername, setAdminUsername] = useState(null)
  const [adminProfilePicture, setAdminProfilePicture] = useState(null)
  const [suggestedMarkers, setSuggestedMarkers] = useState([])
  const [showAdminView, setShowAdminView] = useState(false)
  const [panTo, setPanTo] = useState(undefined)

  const history = useHistory()
  const location = useLocation()

  // Init Firebase
  const config = {
    apiKey: 'AIzaSyBGF-_kiDVID2D9_8A8i4zLtHWYxG704yA',
    authDomain: 'wch-interactive.firebaseapp.com',
    // databaseURL: 'https://restaurants-kids-friendly.europe-west1.firebasedatabase.app/',
    databaseURL: 'https://buvettes.europe-west1.firebasedatabase.app/',
    projectId: 'wch-interactive',
    storageBucket: 'wch-interactive.appspot.com',
    messagingSenderId: '717470310087',
    appId: '1:717470310087:web:c2fb9c194a203ebfee68e2',
    measurementId: 'G-0GB2H3HTS8'
  }
  if (!firebase.apps.length) {
    firebase.initializeApp(config)
  }

  const awaitingReview = suggestedMarkers.filter(
    marker => marker.needsReview && marker.needsReview === true
  )
  const flaggedMarkers = markers.filter(marker =>
    Object.prototype.hasOwnProperty.call(marker, 'flag')
  )
  const disabledMarkers = markers.filter(
    marker => marker.disabled && marker.disabled === true
  )

  const updateMarkers = (markerId, updateType) => {
    console.log('markerId:', markerId, updateType)
    if (updateType === 'reviewed') {
      // Cf. https://stackoverflow.com/a/49502115
      const markerList = [...suggestedMarkers]
      const markerIndex = markerList.findIndex(
        marker => marker.markerId === markerId
      )

      if (markerIndex > 0) {
        const marker = { ...markerList[markerIndex] }
        marker.needsReview = false
        markerList[markerIndex] = marker
        setSuggestedMarkers(markerList)
      }
    }
  }
  const getMarkers = () => {
    const markersList = []
    const statisticsList = [
      { filter: 'filter1', text: 'menu enfant', count: 0 },
      { filter: 'filter2', text: 'espace jeu intérieur', count: 0 },
      { filter: 'filter3', text: 'place de jeux à proximité', count: 0 },
      { filter: 'filter4', text: 'accès pratique pour une poussette', count: 0 }
    ]
    const storedMarkers = window.localStorage.getItem(ARTICLE_ID + '-markers')

    // Use cached data if < 24 hours
    // currently set to 30 seconds
    const cacheInSeconds = 30 // 1000 * 60 * 60 * 24
    if (storedMarkers !== null) {
      const markersData = JSON.parse(storedMarkers)
      if (new Date().getTime() - markersData.ts < cacheInSeconds) {
        console.log(
          'Using cached data – ',
          markersData.ts - new Date().getTime()
        )
        setMarkers(markersData.data)
        // TODO: Active marker?
      } else {
        console.log('Local data is outdated')
      }
    }

    firebase
      .database()
      .ref('markers/')
      .once('value')
      .then(function(snapshot) {
        snapshot.forEach(markerItem => {
          markersList.push(markerItem.val())
        })

        // Save it locally
        localStorage.setItem(
          ARTICLE_ID + '-markers',
          JSON.stringify({ ts: new Date().getTime(), data: markersList })
        )

        setMarkers(markersList)
        const activeMarker = markersList
          ? markersList.find(
              c => c.markerId === location.pathname.split('/')[2]
            )
          : null
        setActiveMarker(activeMarker)

        statisticsList.forEach(item => {
          item.count = markersList.filter(
            marker => marker[item.filter] === true
          ).length
        })
        setStatistics(statisticsList)
      })
  }

  /*
    Admin states and admin tools
  */
  const authStateObserver = user => {
    if (user) {
      // Admin user is signed in
      setIsAdminConnected(true)
      const displayName = getAdminDisplayName()
      const profilePic = firebase.auth().currentUser?.photoURL || null
      if (displayName) {
        setAdminUsername(displayName)
      }
      if (profilePic) {
        setAdminProfilePicture(profilePic)
      }
    } else {
      // No admin user is connected
      setIsAdminConnected(false)
    }
  }
  const toggleAdminView = () => {
    if (showAdminView === false && clientCheckAdminEmail() === true) {
      // load suggested markers
      const suggestedMarkersList = []

      firebase
        .database()
        .ref('suggested_markers/')
        .once('value')
        .then(function(snapshot) {
          snapshot.forEach(markerItem => {
            suggestedMarkersList.push(markerItem.val())
          })
          console.log(suggestedMarkers)
          setSuggestedMarkers(suggestedMarkersList)
        })
    }
    setShowAdminView(!showAdminView)
  }
  firebase.auth().onAuthStateChanged(authStateObserver)

  useEffect(() => {
    console.log('MOUNTED')
    // Create a new user in Firebase if first time the app is opened
    let userId = window.localStorage.getItem(ARTICLE_ID + '-userId')
    if (userId) {
      console.log('This user exists: ' + userId)
      setUserId(userId)
    } else {
      console.log('This user doesn’t exist')
      const newUserEntry = firebase
        .database()
        .ref('stories/' + ARTICLE_ID + '/users/')
        .push({
          publication: parseHostname().publicationName
        })
      userId = newUserEntry.getKey()
      window.localStorage.setItem(ARTICLE_ID + '-userId', userId)
      setUserId(userId)
    }

    const adminConnected = adminUserConnected()
    console.log('Admin connected?', adminConnected)
    setIsAdminConnected(adminConnected)
    getMarkers()
  }, [])

  useEffect(() => {
    // Scroll to the list if coming from the edition screen
    if (location.pathname.split('/')[1] === 'list') {
      window.scrollTo(
        0,
        document.getElementById('map-container').getBoundingClientRect().top +
          window.scrollY
      )
    }
  }, [location.pathname])

  return (
    <div className='index' ref={container}>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <div className='top-line'>Carte collaborative</div>
            <h1>{localisationStrings[locale].title}</h1>
            <h2>{localisationStrings[locale].lead}</h2>
          </div>
        </div>
      </header>

      <Route exact path={['/', '/buvette/:buvetteId', '/list']}>
        <>
          <div className='ui vertical segment' id='intro-text'>
            <div className='ui text container'>
              <div className='ta metainfo'>
                <div className='ta author'>
                  Chloé Din, Paul Ronga, la Rédaction
                </div>
                <div className='ta updated'>
                  {localisationStrings[locale].update}
                </div>
              </div>
              <div className='ta text'>
                <p>
                  Elles ont au minimum une terrasse, des chaises, des tables,
                  voire plus si affinités, toujours sous le soleil exactement.
                  Les buvettes éphémères fleurissent chaque été en terres
                  vaudoises, que ce soit au bord du lac ou dans des endroits
                  plus insolites.
                </p>

                <p>
                  La rédaction vous invite à découvrir ses coups de cœur dans un{' '}
                  <a href='https://24heures.ch/209078437182'>dossier spécial</a>{' '}
                  et à vous emparer de sa carte interactive. Découvrez les
                  adresses répertoriées aux quatre coins du canton et surtout,
                  proposez celles qui animeront votre belle saison.
                </p>
                <div className='ui text container'>
                  <div
                    style={{
                      display: 'block',
                      textAlign: 'left',
                      marginTop: '2em'
                    }}
                  >
                    <ArticleTeaser
                      title='Lire notre dossier:'
                      ids={['209078437182']}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='mapbox-container' id='map-container'>
            <div className='mapbox'>
              <div className='mapbox-menu'>
                <div className='menu-block-space' />
                <div className='menu-block'>
                  <Filters
                    markers={markers}
                    handleFilter={filteredMarkers => {
                      setFilteredMarkers(filteredMarkers)
                    }}
                    locale='fr'
                  />
                </div>
                <div
                  className='menu-block'
                  onClick={() => {
                    setMapIsLocked(!mapIsLocked)
                  }}
                >
                  <div className={`menu-button ${mapIsLocked ? '' : 'active'}`}>
                    <i
                      style={{ fontSize: '0.65em' }}
                      className='ui icon expand arrows alternate'
                    />
                    {mapIsLocked
                      ? isMobile
                        ? 'Naviguer'
                        : 'Naviguer dans la carte'
                      : isMobile
                      ? 'Sortir'
                      : 'Quitter la navigation'}
                  </div>
                  {mapIsLocked && (
                    <a className='ui pointing basic label map-activation-tooltip'>
                      Cliquez ici pour interagir avec la carte
                    </a>
                  )}
                </div>
                <div
                  className='menu-block'
                  onClick={() => {
                    history.replace('/contribuer')
                  }}
                >
                  <div className='menu-button'>
                    <i
                      style={{ fontSize: '0.65em' }}
                      className='ui icon pencil alternate'
                    />{' '}
                    {isMobile ? 'Éditer' : 'Contribuer'}
                  </div>
                </div>
              </div>

              <MapBox
                handleMapLoaded={map => {
                  setMap(map)
                }}
                handleClickMarker={marker => {
                  setActiveMarker(marker)
                  history.replace(`/buvette/${marker.markerId}`)
                }}
                markers={showAdminView ? suggestedMarkers : filteredMarkers}
                activeMarker={activeMarker}
                locked={mapIsLocked}
              />
            </div>

            <RestosList
              restos={filteredMarkers}
              handleClick={marker => {
                setActiveMarker(marker)
                history.replace(`/buvette/${marker.markerId}`)
              }}
            />

            <InfoLayer
              articleId={ARTICLE_ID}
              userId={userId}
              data={markers}
              locale='fr'
            />
          </div>
        </>
      </Route>

      <Route exact path='/contribuer'>
        <>
          <GotoTop />
          <div className='ui vertical segment'>
            <div className='ui text container'>
              <div className='ta text' id='suggestion-text'>
                <p>
                  <b>Proposer un nouveau lieu</b>
                </p>
                <p>
                  Cliquez à l’emplacement désiré pour ajouter une buvette. La
                  validation peut prendre quelques jours, le temps de se
                  renseigner sur l’établissement si nous ne le connaissons pas.
                </p>
              </div>
            </div>
          </div>

          <div className='ui text container'>
            <div
              className='link-back'
              onClick={() => {
                history.replace('/list')
              }}
            >
              <i className='ui icon left chevron' /> Revenir à la liste des
              restaurants
            </div>
          </div>

          <div className='ui vertical segment very fitted'>
            <div id='mapSection'>
              <CollaborativeMap
                locale={locale}
                markers={showAdminView ? suggestedMarkers : markers}
                showAdminView={showAdminView}
                userId={userId}
                articleId={ARTICLE_ID}
                panTo={panTo}
                handleUpdateMarkers={updateMarkers}
                handleMapLoaded={map => {
                  setMap(map)
                }}
              />
              <AdminToolbar
                locale={locale}
                isConnected={isAdminConnected}
                username={adminUsername}
                profilePicture={adminProfilePicture}
                adminViewOn={showAdminView}
                handleSignIn={signIn}
                handleSignOut={signOut}
                handleToggleAdminView={toggleAdminView}
              />
            </div>
          </div>

          {/* FLAGGED MARKERS */}
          {isAdminConnected && (
            <div
              className={
                'ui vertical segment ' +
                (showAdminView === false ? 'visible' : 'hidden')
              }
            >
              <AdminMarkersList
                markers={flaggedMarkers}
                suffix='flagged'
                countLabel={localisationStrings[locale].flaggedPlaces}
                noMarkersMessage={localisationStrings[locale].noFlaggedPlaces}
                adminUsername={adminUsername}
                handlePanTo={setPanTo}
                handleDisable={adminDisableMarker}
                handleDelete={adminDeleteMarker}
              />
              <AdminMarkersList
                markers={disabledMarkers}
                suffix='disabled'
                countLabel={localisationStrings[locale].disabledPlaces}
                noMarkersMessage={localisationStrings[locale].noDisabledPlaces}
                adminUsername={adminUsername}
                handlePanTo={setPanTo}
                handleDisable={adminDisableMarker}
                handleDelete={adminDeleteMarker}
              />
            </div>
          )}

          {/* SUGGESTED MARKERS */}
          <div
            className={
              'ui vertical segment ' +
              (showAdminView === true ? 'visible' : 'hidden')
            }
          >
            <AdminMarkersList
              markers={suggestedMarkers}
              suffix='suggested'
              countLabel={localisationStrings[locale].suggestedPlaces}
              noMarkersMessage={localisationStrings[locale].noSuggestedPlaces}
              adminUsername={adminUsername}
              handlePanTo={setPanTo}
              handleDisable={adminDisableMarker}
              handleDelete={adminDeleteMarker}
            />
          </div>
        </>
      </Route>

      <Footer
        articleId={ARTICLE_ID}
        hidePortfolioTeaser
        title='Comment notez-vous ce contenu?'
        questions={[
          'La mise en œuvre technique a-t-elle fonctionné?',
          'La mise en forme vous plaît-elle?',
          'Voudriez-vous voir davantage de ce type de contenu?'
        ]}
        locale='fr'
        mailTo='interactif@tamedia.ch'
        credits={[
          ['Conception & réalisation', 'Paul Ronga, Mathieu Rudaz'],
          ['Fond de carte', 'Mapbox, contributeurs OpenStreetMap']
        ]}
      />
    </div>
  )
}

const GotoTop = () => {
  useEffect(() => {
    window.scrollTo({
      top:
        document.getElementById('suggestion-text').getBoundingClientRect().top +
        window.scrollY,
      behavior: 'smooth'
    })
  }, [])

  return null
}
